/**
 * useKeybindings
 *
 * Attaches locally-scoped keybindings to a control via a returned `onKeyDown`.
 * Keys stay declared once in the registry (so the cheat sheet and
 * `aria-keyshortcuts` stay in sync); the control supplies only the handlers for
 * the ids it owns — e.g. a reference field owns `field.openDetail` (open the
 * record sidebar) and `field.openPopup` (open the dropdown). The first matching
 * binding wins and stops propagation so it never double-fires with container
 * navigation.
 */

import { useCallback } from 'react';
import { keybindingRegistry } from './keybindingRegistry';
import { matchChord } from './matchChord';
import type { KeybindingId } from './keybinding.types';

export function useKeybindings(
  handlers: Partial<Record<KeybindingId, () => void>>
): { onKeyDown: (e: React.KeyboardEvent) => void } {
  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.defaultPrevented) return;
    for (const id of Object.keys(handlers) as KeybindingId[]) {
      const handler = handlers[id];
      if (!handler) continue;
      const binding = keybindingRegistry.byId(id);
      if (!binding) continue;
      if (binding.chords.some((c) => matchChord(e, c))) {
        e.preventDefault();
        e.stopPropagation();
        handler();
        return;
      }
    }
  }, [handlers]);
  return { onKeyDown };
}
